import React from 'react';
import styled from 'styled-components'; 
import StressedManImage from '../../../Assets/frontendImages/struggle person.svg'; 
import FeaturesCarousel from "../../UIs/FeaturesCarousel.jsx";
import PublicIcon from "@mui/icons-material/Public";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import StarsIcon from "@mui/icons-material/Stars";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import Features from '../../HomePage/Features/Features.jsx';
import MoreFeatures from "../../UIs/MoreFeatures";

const MainWrapper = styled.div`
  background-color: var(--white);
  padding: 3rem 1rem;
  display: flex;
  justify-content: center;

  @media (max-width: 576px) {
    padding: 1.5rem 1rem;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center; 
  justify-content: space-between;
  max-width: 1200px;
  width: 100%;
  gap: 60px;

  @media (max-width: 960px) {
    flex-direction: column;
    text-align: center; 
  }
`;

const TextColumn = styled.div`
  flex: 1;
  max-width: 550px;
  display: flex;
  flex-direction: column;

  @media (max-width: 960px) {
    align-items: center; 
    margin: 0 auto;
  }
`;

const Heading = styled.h2`
  font-size: 36px; 
  font-weight: 600;
  color:var(--font1);
  line-height: 1.2;
  margin-bottom: 24px;

  @media (max-width: 960px) {
    font-size: 32px;
  }

  @media (max-width: 576px) {
    font-size: 24px;
  }
`;

const SubText = styled.p`
  font-size: 16px;
  color:var(--font1);
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 960px) {
    align-items: flex-start; 
    text-align: left;
    margin: 0 auto; 
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: center; 
  font-size: 16px;
  color:var(--font1);
`;

const ImageColumn = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;

  @media (max-width: 960px) {
    display: none;
  }
`;

const MainImage = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  object-fit: contain;
`;


const MainSection = () => {
  const items = [
    {
      icon: <PeopleAltIcon sx={{ fontSize: 24 }} />,
      text: "10,000+ Active Freelancers",
    },
    {
      icon: <PublicIcon sx={{ fontSize: 24 }} />,
      text: "50+ Countries Onboarded",
    },
    {
      icon: <BusinessCenterIcon sx={{ fontSize: 24 }} />,
      text: "25+ Industries Served",
    },
    {
      icon: <StarsIcon sx={{ fontSize: 24 }} />,
      text: "4.9/5 User Satisfaction",
    },
  ];

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 4000,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    pauseOnHover: false,
    cssEase: "linear",
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 4 } },
      { breakpoint: 992, settings: { slidesToShow: 2.5 } },
      { breakpoint: 768, settings: { slidesToShow: 2 } },
      { breakpoint: 576, settings: { slidesToShow: 1.1 } },
    ],
  };

  const data = [
    {
      Image: "/images/more features/Client & Project Managment/ProjectDocumentation.svg",
      title: "Project Documentation",
      desc: "Keep every detail clear. Add notes and key updates directly in the project to ensure you stay aligned from start to end.",
    },
    {
      Image: "/images/more features/tasks/SmartAlerts.svg",
      title: "Smart Alerts",
      desc: "Get real-time reminders for deadlines and updates. Zodot keeps you informed so you never miss an important task.",
    },
    {
      Image: "/images/more features/invoices/DownloadInvoicesWithEase.svg",
      title: "Download Invoices with Ease",
      desc: "Download or export invoices in seconds. Keep every record organized, share effortlessly, and access billing data anytime.",
    },
  ];
  
  const PurpleCheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '24px', marginRight: '16px' }}>
      <circle cx="12" cy="12" r="12" fill="var(--brandColor)" /> 
      <path d="M7 12L10.5 15.5L17 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  const mainPageTitles = [
  "Stay on Top of Every Task",           // Tab 0: Tasks
  "Manage Clients & Projects Seamlessly", // Tab 1: Clients
  "Get Paid Faster with Smart Invoicing",// Tab 2: Invoices
  "Seal Every Deal with Branded Contracts", // Tab 3: Agreements
  "Track Every Penny with Zero Stress",  // Tab 4: Transactions
  "Financial Reports in Seconds",        // Tab 5: Statements
  "Capture Every Billable Minute"        // Tab 6: Time Tracking
];

  return (
    <div>
      <FeaturesCarousel items={items} sliderSettings={sliderSettings} />
      <MainWrapper>
        <Container>
          
          <TextColumn data-aos="fade-right">
            <Heading>
              Still Juggling Work<br />
              Across too Many<br />
              Apps?
            </Heading>
            <SubText>
              From tracking time to sending invoices, you're constantly dealing with:
            </SubText>

            <ListContainer>
              <ListItem>
                <PurpleCheckIcon /> Jumping between multiple tools
              </ListItem>
              <ListItem>
                <PurpleCheckIcon /> Forgetting to track billable hours
              </ListItem>
              <ListItem>
                <PurpleCheckIcon /> Dealing with confusing or delayed invoices
              </ListItem>
              <ListItem>
                <PurpleCheckIcon /> Struggling to see everything in one place
              </ListItem>
            </ListContainer>
          </TextColumn>

          <ImageColumn data-aos="fade-left">
            <MainImage 
              src={StressedManImage} 
              alt="Stressed person juggling apps" 
            />
          </ImageColumn>

        </Container>
      </MainWrapper>
      
      <Features 
        Heading='Manage Projects, Time, and Clients in One Place'
        textFirstOnMobile={true}
        showButton={false}
        customTitles={mainPageTitles}
      />
      <MoreFeatures data={data} />
    </div>
  );
};

export default MainSection;